/* ============================================================================
 * MK4U CART CONVERSION LAYER  —  assets/mk4u-cart-conversion.js
 * ----------------------------------------------------------------------------
 * Built for: Expanse v8.0.0 (Archetype Themes)
 *
 * DESIGN NOTE — why this is a mount-in layer, not a custom drawer:
 * Expanse already ships <header-cart-drawer> / <toggle-cart> with a populated
 * .cart-drawer, item thumbnails, and a <quantity-selector> web component. That
 * drawer is accessible (focus trap, Esc, click-outside) and updates itself.
 * Rebuilding it would mean re-solving all of that and re-breaking it on every
 * theme update. So this file only ADDS what the theme doesn't have.
 *
 * WHAT IT ADDS
 *   1. Gift/threshold progress bar at the top of the drawer
 *   2. Auto-applies the gift discount code at checkout once unlocked
 *   3. Reassurance line + delivery estimate directly above the checkout button
 *   4. Auto-opens the drawer on add-to-cart (covers app-injected add buttons
 *      like Zoorix, which bypass the theme's own handlers)
 *   5. Sticky add-to-cart bar on mobile product pages
 *   6. CTA colour on the product add-to-cart and the drawer checkout button
 *   7. A more inviting search placeholder
 *
 * It is defensive throughout: every mount point is optional, and if a selector
 * isn't found the feature silently no-ops rather than throwing. Nothing here
 * blocks add-to-cart.
 * ==========================================================================*/

(function () {
  'use strict';

  var CFG = window.MK4U_CART_CONFIG || {};

  /* -- Selectors, all confirmed against the live Expanse v8 DOM ----------- */
  var SEL = {
    drawer:       '.cart-drawer',
    drawerForm:   '.cart__drawer-form',
    scrollable:   '.cart__scrollable',
    items:        '.cart__items',
    footer:       '.cart__footer',
    toggleCart:   'toggle-cart',
    emptyState:   '.site-header__cart-empty'
  };

  /* ======================================================================
   * Utilities
   * ==================================================================== */

  function $(sel, root) { return (root || document).querySelector(sel); }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /**
   * Format an amount in cents using the shop's money format.
   * Falls back to a plain "Rs.N,NNN" if the format string is unusual.
   */
  function money(cents) {
    var amount = Math.max(0, Math.round(cents) / 100);
    var whole = amount.toFixed(amount % 1 === 0 ? 0 : 2);
    var grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var fmt = CFG.moneyFormat || 'Rs.{{amount}}';
    // Shopify money formats use {{amount}}, {{amount_no_decimals}}, etc.
    if (/\{\{\s*amount/.test(fmt)) {
      return fmt.replace(/\{\{\s*amount[^}]*\}\}/, grouped);
    }
    return 'Rs.' + grouped;
  }

  function fetchCart() {
    return fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  /**
   * Returns the ONE real product-form add-to-cart button, or null.
   * Quick-add tiles in product grids reuse the .add-to-cart class, so we
   * always resolve via the product form and never by class alone.
   */
  function getPrimaryAtc() {
    if (!CFG.isProductPage) return null;
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return null;
    return form.querySelector('[type="submit"], button[name="add"]');
  }

  /**
   * Paint an Expanse button with our CTA colour.
   *
   * Setting the theme's own --color-primary on the element is what actually
   * sticks. Expanse injects its component CSS at RUNTIME (see <style-sheet>
   * / utility.stylesheet.js), so it lands after our <style> block and won a
   * plain `background !important` fight. Overriding the variable instead
   * means the theme derives background, hover shade and border from our
   * colour, so every state stays consistent. Verified in the live DOM.
   */
  function paintCta(el) {
    if (el) el.style.setProperty('--color-primary', CFG.ctaColor || '#1f2027');
  }

  /* ======================================================================
   * 1 + 2. Threshold progress bar, and discount auto-apply
   * ==================================================================== */

  function renderProgress(cart) {
    var form = $(SEL.drawerForm);
    if (!form) return;

    var threshold = CFG.giftThresholdCents || 0;
    if (!threshold) return;

    var total = cart.total_price || 0;
    var unlocked = total >= threshold;
    var remaining = Math.max(0, threshold - total);
    var pct = Math.min(100, threshold ? (total / threshold) * 100 : 0);

    var el = $('.mk4u-cc__progress', form);
    if (!el) {
      el = document.createElement('div');
      el.className = 'mk4u-cc mk4u-cc__progress';
      // Announce threshold changes to screen readers, but politely — this
      // updates on every quantity tweak.
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      var scrollable = $(SEL.scrollable, form);
      if (scrollable && scrollable.parentNode) {
        scrollable.parentNode.insertBefore(el, scrollable);
      } else {
        form.insertBefore(el, form.firstChild);
      }
    }

    el.classList.toggle('mk4u-cc--unlocked', unlocked);
    el.innerHTML =
      '<p class="mk4u-cc__progress-msg">' +
        (unlocked
          ? escapeHtml(CFG.giftUnlockedMsg || 'Gift unlocked')
          : 'Spend <em>' + money(remaining) + '</em> more to get ' +
            escapeHtml(CFG.giftRewardText || 'a free gift')) +
      '</p>' +
      '<div class="mk4u-cc__track">' +
        '<div class="mk4u-cc__fill" style="width:' + pct.toFixed(1) + '%"></div>' +
      '</div>';

    applyGiftCode(form, unlocked);
  }

  /**
   * Shopify's cart form accepts a `discount` input, which carries the code
   * through to checkout. This means the customer never has to remember
   * "mk4u20" — which is the whole reason the current popup code underperforms.
   */
  function applyGiftCode(form, unlocked) {
    var code = CFG.giftCode;
    var input = form.querySelector('input[name="discount"].mk4u-cc__discount');

    if (!code || !unlocked) {
      if (input && input.parentNode) input.parentNode.removeChild(input);
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'discount';
      input.className = 'mk4u-cc__discount';
      form.appendChild(input);
    }
    input.value = code;
  }

  /* ======================================================================
   * 3. Reassurance line + delivery estimate
   *
   * Sits directly ABOVE the checkout action — the point where hesitation
   * actually happens, and where it cannot be clipped by the drawer's
   * max-height. Rendered once; it doesn't depend on cart contents.
   * ==================================================================== */

  function renderReassurance() {
    var form = $(SEL.drawerForm);
    if (!form || $('.mk4u-cc__reassure', form)) return;

    var badges = (CFG.badges || []).filter(Boolean);
    var html = '';

    if (CFG.deliveryEstimate) {
      html +=
        '<div class="mk4u-cc__delivery">' +
          '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" ' +
               'stroke="currentColor" stroke-width="3" aria-hidden="true">' +
            '<path d="M5 13l4 4L19 7"/>' +
          '</svg>' +
          '<span>' + escapeHtml(CFG.deliveryEstimate) + '</span>' +
        '</div>';
    }
    // One compact dot-separated line rather than a bulleted list. The list
    // version measured 122px tall inside a 514px drawer - height the item
    // list needs far more than the badges do.
    if (badges.length) {
      html += '<p class="mk4u-cc__badges">' +
        badges.map(escapeHtml).join(' &middot; ') +
        '</p>';
    }
    if (!html) return;

    var frag = document.createElement('div');
    frag.className = 'mk4u-cc mk4u-cc__reassure';
    frag.innerHTML = html;

    // Insert ABOVE the footer. Appending to the form put this below the
    // checkout button, where the drawer's max-height clipped it off screen.
    var footer = $(SEL.footer, form);
    if (footer) {
      form.insertBefore(frag, footer);
    } else {
      form.appendChild(frag);
    }
  }

  /**
   * Drawer checkout button, matched to the product-page CTA. Expanse rebuilds
   * the drawer's HTML on every cart change, so this re-runs from
   * scheduleRender rather than only once at boot.
   */
  function enhanceDrawerCheckout() {
    var form = $(SEL.drawerForm);
    if (!form) return;
    paintCta(form.querySelector('[name="checkout"]') ||
             form.querySelector('.cart__checkout-wrapper button'));
  }

  /* ======================================================================
   * Re-render orchestration
   *
   * Expanse refreshes the drawer's inner HTML when the cart changes, which
   * wipes anything we injected. Rather than guess the theme's event names, we
   * observe the drawer for mutations and re-mount. Debounced so a burst of
   * DOM writes only costs one render.
   * ==================================================================== */

  var renderTimer = null;
  function scheduleRender() {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(function () {
      fetchCart().then(function (cart) {
        renderProgress(cart);
        renderReassurance();
        enhanceDrawerCheckout();
      }).catch(function () { /* never block the cart on our own failure */ });
    }, 90);
  }

  function watchDrawer() {
    var drawer = $(SEL.drawer);
    if (!drawer) return;

    var mo = new MutationObserver(function (records) {
      // Ignore mutations we caused ourselves, or we'd loop forever.
      var external = records.some(function (r) {
        return !(r.target.closest && r.target.closest('.mk4u-cc'));
      });
      if (external) scheduleRender();
    });
    mo.observe(drawer, { childList: true, subtree: true });
  }

  /* ======================================================================
   * 4. Auto-open the drawer on add-to-cart
   *
   * The Expanse cart_type "dropdown" setting handles this for the theme's own
   * add buttons. This patch additionally covers add-to-cart calls made by
   * installed apps (Zoorix upsells, quick-add tiles) which bypass the theme's
   * handlers and would otherwise add silently with no feedback.
   *
   * Open state lives on <header-section class="cart-open"> — confirmed by
   * inspecting the live drawer, not guessed.
   * ==================================================================== */

  function isDrawerOpen() {
    var hs = document.querySelector('header-section');
    return !!(hs && hs.classList.contains('cart-open'));
  }

  function openDrawer() {
    var toggle = $(SEL.toggleCart);
    if (!toggle) return;
    // <toggle-cart> responds to a click on its interactive child.
    var btn = toggle.querySelector('button, a') || toggle;
    btn.click();
  }

  function patchFetchToOpenDrawer() {
    if (!window.fetch || window.__mk4uFetchPatched) return;
    window.__mk4uFetchPatched = true;

    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) { /* ignore */ }

      var isAdd = /\/cart\/add/.test(url);

      return nativeFetch.apply(this, arguments).then(function (res) {
        if (isAdd && res && res.ok) {
          // Let the theme finish its own drawer refresh first.
          setTimeout(function () {
            if (!isDrawerOpen()) openDrawer();
            scheduleRender();
          }, 220);
        }
        return res;
      });
    };
  }

  /* ======================================================================
   * 6. Primary add-to-cart prominence
   *
   * Tags the real button with a class rather than styling `.add-to-cart`
   * globally, so quick-add buttons in collection grids are untouched.
   * ==================================================================== */

  function enhancePrimaryAtc() {
    var btn = getPrimaryAtc();
    if (!btn) return;
    btn.classList.add('mk4u-primary-atc');
    paintCta(btn);
  }

  /* ======================================================================
   * 7. Search prompt
   *
   * Only the placeholder text is changed. The field's colours are left to
   * the theme: its text is white on a dark header pill, so overriding the
   * background here would risk white-on-white typed text.
   * ==================================================================== */

  function enhanceSearch() {
    if (!CFG.searchPlaceholder) return;
    Array.prototype.forEach.call(
      document.querySelectorAll('input[name="q"]'),
      function (input) { input.setAttribute('placeholder', CFG.searchPlaceholder); }
    );
  }

  /* ======================================================================
   * 5. Sticky add-to-cart bar (mobile product pages)
   *
   * Deliberately a sticky ATC bar and NOT a floating cart bubble. On a phone
   * the thumb zone is scarce: a persistent cart bubble competes with the one
   * button that actually converts, and tends to cover content. This bar shows
   * once the real add-to-cart button has scrolled out of view, and it clicks
   * the theme's own button so variant selection, inventory rules, and app
   * hooks all keep working.
   * ==================================================================== */

  function initStickyAtc() {
    // Built at every width and hidden with CSS above 768px, so a rotation or
    // window resize still behaves. Bailing out on width here meant the bar
    // never existed for anyone who loaded the page on a wide viewport first.
    var realBtn = getPrimaryAtc();
    if (!realBtn) return;

    var titleEl = document.querySelector('.product-single__title, h1');
    var title = titleEl ? titleEl.textContent.trim() : '';

    var bar = document.createElement('div');
    bar.className = 'mk4u-sticky-atc';
    bar.innerHTML =
      '<div class="mk4u-sticky-atc__meta">' +
        '<span class="mk4u-sticky-atc__title">' + escapeHtml(title) + '</span>' +
        '<span class="mk4u-sticky-atc__price" data-mk4u-price></span>' +
      '</div>' +
      '<button type="button" class="mk4u-sticky-atc__btn">Add to cart</button>';
    document.body.appendChild(bar);

    // Mirror the live price node so variant switches stay in sync.
    var priceSource = document.querySelector('[data-product-price], .product__price, .price');
    var priceTarget = bar.querySelector('[data-mk4u-price]');
    function syncPrice() {
      if (priceSource && priceTarget) priceTarget.innerHTML = priceSource.innerHTML;
    }
    syncPrice();
    if (priceSource) {
      new MutationObserver(syncPrice)
        .observe(priceSource, { childList: true, subtree: true, characterData: true });
    }

    // Mirror disabled/sold-out state from the real button.
    var proxyBtn = bar.querySelector('.mk4u-sticky-atc__btn');
    function syncState() {
      proxyBtn.disabled = !!realBtn.disabled;
      if (realBtn.disabled) proxyBtn.textContent = 'Sold out';
    }
    syncState();
    new MutationObserver(syncState).observe(realBtn, { attributes: true });

    // Delegate to the theme's real button so nothing is bypassed.
    proxyBtn.addEventListener('click', function () { realBtn.click(); });

    // Show the bar only while the real button is off screen.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        bar.classList.toggle('is-visible', !entries[0].isIntersecting);
      }, { rootMargin: '0px 0px -40px 0px' }).observe(realBtn);
    }
  }

  /* ======================================================================
   * Boot
   * ==================================================================== */

  function init() {
    patchFetchToOpenDrawer();
    watchDrawer();
    enhancePrimaryAtc();
    enhanceSearch();
    initStickyAtc();
    scheduleRender();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
